<script lang="ts">
  import { onMount } from 'svelte';
  
  let canvas: HTMLCanvasElement;
  let ctx: CanvasRenderingContext2D;
  let animationId: number;
  let initialized = false;
  
  interface Line {
    x: number;
    y: number;
    length: number;
    speed: number;
    opacity: number;
    thickness: number;
    originalX: number;
    originalY: number;
  }
  
  let lines: Line[] = [];
  const numberOfLines = 15;
  
  function createLine(useInitialPosition = true): Line {
    const x = Math.random() * (window.innerWidth + 200);
    const y = useInitialPosition ? -100 : Math.random() * window.innerHeight;
    return {
      x,
      y,
      length: 100 + Math.random() * 300,
      speed: 0.3 + Math.random() * 0.7,
      opacity: 0.3 + Math.random() * 0.4,
      thickness: 1 + Math.random() * 2,
      originalX: x,
      originalY: y
    };
  }
  
  function initLines() {
    if (initialized) return;
    
    lines = [];
    for (let i = 0; i < numberOfLines; i++) {
      const line = createLine(false);
      lines.push(line);
    }
    
    initialized = true;
  }
  
  function animate() {
    if (!ctx || !canvas) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    lines.forEach((line, index) => {
      line.x -= line.speed;
      line.y += line.speed * 0.8;
      
      // Reset line if it goes off screen
      if (line.x < -line.length || line.y > window.innerHeight + 100) {
        const newLine = createLine();
        newLine.length = line.length;
        newLine.speed = line.speed;
        newLine.opacity = line.opacity;
        newLine.thickness = line.thickness;
        lines[index] = newLine;
      }
      
      ctx.save();
      
      ctx.shadowColor = '#5AD197';
      ctx.shadowBlur = 20;
      ctx.globalAlpha = line.opacity * 0.3;
      ctx.strokeStyle = '#5AD197';
      ctx.lineWidth = line.thickness + 4;
      ctx.lineCap = 'round';
      
      ctx.beginPath();
      ctx.moveTo(line.x, line.y);
      ctx.lineTo(line.x - line.length * 0.8, line.y + line.length * 0.6);
      ctx.stroke();
      
      ctx.shadowBlur = 0;
      ctx.globalAlpha = line.opacity;
      ctx.strokeStyle = '#7DD9B0';
      ctx.lineWidth = line.thickness;
      
      ctx.beginPath();
      ctx.moveTo(line.x, line.y);
      ctx.lineTo(line.x - line.length * 0.8, line.y + line.length * 0.6);
      ctx.stroke();
      
      ctx.restore();
    });
    
    animationId = requestAnimationFrame(animate);
  }
  
  function resizeCanvas() {
    if (!canvas) return;
    
    const oldWidth = canvas.width;
    const oldHeight = canvas.height;
    
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    if (initialized) {
      lines.forEach(line => {
        const widthRatio = canvas.width / oldWidth;
        const heightRatio = canvas.height / oldHeight;
        
        if (oldWidth > 0 && oldHeight > 0) {
          line.x = line.x * widthRatio;
          line.y = line.y * heightRatio;
        }
      });
    } else {
      initLines();
    }
  }
  
  onMount(() => {
    ctx = canvas.getContext('2d')!;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    initLines();
    animate();
    
    window.addEventListener('resize', resizeCanvas);
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  });
</script>

<canvas
  bind:this={canvas}
  class="fixed top-0 left-0 w-full h-full pointer-events-none z-0"
  style="background: linear-gradient(135deg, #0a0f0d 0%, #0f1512 100%);"
></canvas>

<style>
  canvas {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: -1;
  }
</style>